import { endOfMonth, getLocalTimeZone, startOfMonth, toCalendarDate, today } from '@internationalized/date';
import Button from '@orn/shared-web/components/Button/Button';
import { CalendarCell, DateInput, RangePresetButton } from '@orn/shared-web/components/DatePicker';
import Dropdown from '@orn/shared-web/components/Dropdown';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { useControlledState } from '@react-stately/utils';
import * as React from 'react';
import { useDateFormatter } from 'react-aria';
import {
  Button as AriaButton,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  DateRangePicker as AriaDateRangePicker,
  Dialog as AriaDialog,
  type DateValue,
  DialogTrigger,
  Group as AriaGroup,
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarStateContext,
} from 'react-aria-components';

import { OLDEST_TRANSACTION_DATE_FALLBACK, useOldestTransactionDate } from '../../providers/TransactionsProvider';

import type { CalendarDate } from '@internationalized/date';
import type { CalendarDateRange } from '@orn/shared-web/components/DatePicker';

// Helper to ensure date doesn't exceed today
const clampToToday = (date: CalendarDate, today: CalendarDate): CalendarDate => {
  return date.compare(today) > 0 ? today : date;
};

// Preset definitions factory - takes today as parameter and optional oldest transaction date for allTime
export const createPresets = (now: CalendarDate, oldestTransactionDate?: CalendarDate) => ({
  thisMonth: {
    label: 'This month',
    getValue: () => ({
      start: startOfMonth(now),
      end: clampToToday(endOfMonth(now), now),
    }),
  },
  pastMonth: {
    label: 'Last month',
    getValue: () => {
      const pastMonthEnd = endOfMonth(now).subtract({ months: 1 });
      return {
        start: startOfMonth(now).subtract({ months: 1 }),
        end: clampToToday(pastMonthEnd, now),
      };
    },
  },
  past3Months: {
    label: '3 months',
    getValue: () => ({
      start: startOfMonth(now).subtract({ months: 2 }),
      end: clampToToday(endOfMonth(now), now),
    }),
  },
  past6Months: {
    label: '6 months',
    getValue: () => ({
      start: startOfMonth(now).subtract({ months: 5 }),
      end: clampToToday(endOfMonth(now), now),
    }),
  },
  thisYear: {
    label: 'This year',
    getValue: () => {
      const yearEnd = endOfMonth(now.set({ month: 12 }));
      return {
        start: startOfMonth(now.set({ month: 1 })),
        end: clampToToday(yearEnd, now),
      };
    },
  },
  pastYear: {
    label: 'Last year',
    getValue: () => {
      const pastYearEnd = endOfMonth(now.set({ month: 12 }).subtract({ years: 1 }));
      return {
        start: startOfMonth(now.set({ month: 1 }).subtract({ years: 1 })),
        end: clampToToday(pastYearEnd, now),
      };
    },
  },
  allTime: {
    label: 'All data',
    getValue: () => ({
      start: oldestTransactionDate ?? OLDEST_TRANSACTION_DATE_FALLBACK,
      end: now,
    }),
  },
});

// Helper to get preset date ranges
export const getPresetDateRange = (preset: PresetDateRangeKey, oldestTransactionDate?: CalendarDate) => {
  const now = today(getLocalTimeZone());
  const presets = createPresets(now, oldestTransactionDate);
  return presets[preset].getValue();
};

/**
 * Determines which preset a date range matches, if any
 * Uses the same logic as getDateRangeDisplayText to ensure consistency
 */
export const isPresetDateRange = (
  dateRange: CalendarDateRange,
  preset: PresetDateRangeKey,
  oldestTransactionDate?: CalendarDate
): boolean => {
  const presetRange = getPresetDateRange(preset, oldestTransactionDate);
  return dateRange?.start.compare(presetRange.start) === 0 && dateRange?.end.compare(presetRange.end) === 0;
};

/**
 * Get the preset label for a date range, if it matches any preset
 * Returns the preset label if it matches, otherwise null
 */
const getPresetLabel = (
  dateRange?: CalendarDateRange,
  defaultPreset?: PresetDateRangeKey,
  oldestTransactionDate?: CalendarDate
): string | null => {
  if (!dateRange) {
    const now = today(getLocalTimeZone());
    const presets = createPresets(now, oldestTransactionDate);
    return defaultPreset ? presets[defaultPreset].label : null;
  }

  // Calculate today to check against presets
  const now = today(getLocalTimeZone());
  const presets = createPresets(now, oldestTransactionDate);

  // Check if the current date range matches any preset
  for (const preset of Object.values(presets)) {
    const presetValue = preset.getValue();
    if (dateRange.start.compare(presetValue.start) === 0 && dateRange.end.compare(presetValue.end) === 0) {
      return preset.label;
    }
  }

  return null;
};

/**
 * Get the formatted date range string
 * Format: "Oct 25 - Nov 5, 2025" (with space after dash)
 * For full months: "Mar 2025"
 * For multiple full months: "Feb, 2024 - Mar, 2024"
 */
export const getFormattedDateRange = (dateRange?: CalendarDateRange): string => {
  if (!dateRange) {
    return '';
  }

  // Check if start is first day of month and end is last day of month
  const startIsFirstOfMonth = dateRange.start.day === 1;
  const endIsLastOfMonth = dateRange.end.day === endOfMonth(dateRange.end).day;

  // Check if it's a single full month (start is first day, end is last day, same month)
  const isFullMonth =
    startIsFirstOfMonth &&
    endIsLastOfMonth &&
    dateRange.start.month === dateRange.end.month &&
    dateRange.start.year === dateRange.end.year;

  // Check if it spans multiple full months (start is first day, end is last day, different months)
  const isMultipleFullMonths =
    startIsFirstOfMonth &&
    endIsLastOfMonth &&
    (dateRange.start.month !== dateRange.end.month || dateRange.start.year !== dateRange.end.year);

  if (isFullMonth) {
    // Format as month and year: "Mar 2025"
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    });
    return formatter.format(dateRange.start.toDate(getLocalTimeZone()));
  }

  if (isMultipleFullMonths) {
    // Format as "Feb, 2024 - Mar, 2024"
    const monthFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
    });
    const yearFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
    });
    const startMonth = monthFormatter.format(dateRange.start.toDate(getLocalTimeZone()));
    const startYear = yearFormatter.format(dateRange.start.toDate(getLocalTimeZone()));
    const endMonth = monthFormatter.format(dateRange.end.toDate(getLocalTimeZone()));
    const endYear = yearFormatter.format(dateRange.end.toDate(getLocalTimeZone()));
    return `${startMonth}, ${startYear} - ${endMonth}, ${endYear}`;
  }

  // Default format: "Oct 25, 2025 - Nov 5, 2025"
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedStartDate = formatter.format(dateRange.start.toDate(getLocalTimeZone()));
  const formattedEndDate = formatter.format(dateRange.end.toDate(getLocalTimeZone()));
  return `${formattedStartDate} - ${formattedEndDate}`;
};

const CalendarMonthTitle = () => {
  const context = React.useContext(RangeCalendarStateContext);
  const formatter = useDateFormatter({
    month: 'short',
    year: 'numeric',
    calendar: context?.visibleRange.start.calendar.identifier,
    timeZone: context?.timeZone,
  });

  if (!context) {
    return null;
  }

  return <>{formatter.format(context.visibleRange.start.toDate(context.timeZone))}</>;
};

export type { CalendarDateRange } from '@orn/shared-web/components/DatePicker';

export type PresetDateRangeKey =
  | 'thisMonth'
  | 'pastMonth'
  | 'past3Months'
  | 'past6Months'
  | 'thisYear'
  | 'pastYear'
  | 'allTime';

export interface IDateRangePickerProps {
  /** The selected date range */
  value?: CalendarDateRange;
  /** The default preset to use (only sets initial date range) */
  defaultValue?: PresetDateRangeKey;
  /** Callback when the date range changes */
  onChange?: (range: CalendarDateRange) => void;
}

const DateRangePicker = ({ value: valueProp, defaultValue = 'thisMonth', onChange }: IDateRangePickerProps) => {
  // Calculate today on each render
  const now = React.useMemo(() => today(getLocalTimeZone()), []);
  const earliestValidDate = React.useMemo(() => now.subtract({ years: 25 }), [now]);
  const oldestTransactionDate = useOldestTransactionDate();

  const presets = React.useMemo(() => {
    const presetDefs = createPresets(now, oldestTransactionDate);
    return {
      thisMonth: {
        label: presetDefs.thisMonth.label,
        value: presetDefs.thisMonth.getValue(),
      },
      pastMonth: {
        label: presetDefs.pastMonth.label,
        value: presetDefs.pastMonth.getValue(),
      },
      past3Months: {
        label: presetDefs.past3Months.label,
        value: presetDefs.past3Months.getValue(),
      },
      past6Months: {
        label: presetDefs.past6Months.label,
        value: presetDefs.past6Months.getValue(),
      },
      thisYear: {
        label: presetDefs.thisYear.label,
        value: presetDefs.thisYear.getValue(),
      },
      pastYear: {
        label: presetDefs.pastYear.label,
        value: presetDefs.pastYear.getValue(),
      },
      allTime: {
        label: presetDefs.allTime.label,
        value: presetDefs.allTime.getValue(),
      },
    };
  }, [now, oldestTransactionDate]);

  // Initialize with default preset's date range if no value provided
  const defaultDateRange = getPresetDateRange(defaultValue, oldestTransactionDate);
  const [value, setValue] = useControlledState(valueProp, defaultDateRange, onChange);
  const [isOpen, setIsOpen] = React.useState(false);
  const [showCustomPicker, setShowCustomPicker] = React.useState(false);

  // Track which preset was selected (null means custom range)
  // Initialize with defaultValue for uncontrolled, or defaultValue if controlled value matches it
  const [selectedPresetKey, setSelectedPresetKey] = React.useState<PresetDateRangeKey | null>(() => {
    if (!valueProp) {
      return defaultValue;
    }
    const defaultPresetValue = presets[defaultValue].value;
    if (
      valueProp.start.compare(defaultPresetValue.start) === 0 &&
      valueProp.end.compare(defaultPresetValue.end) === 0
    ) {
      return defaultValue;
    }
    return null;
  });

  // Internal state for the DateValue used by react-aria-components
  const [internalDateValue, setInternalDateValue] = React.useState<{
    start: DateValue;
    end: DateValue;
  } | null>(() => (value ? { start: value.start, end: value.end } : defaultDateRange));

  // Keep internalDateValue in sync with value
  React.useEffect(() => {
    if (value) {
      setInternalDateValue({ start: value.start, end: value.end });
    }
  }, [value]);

  // Sync selectedPresetKey when value changes (e.g., from chart bar clicks)
  // If value matches current preset, keep it; otherwise try to find a matching preset
  React.useEffect(() => {
    if (!value) {
      return;
    }

    // If current selection still matches, nothing to do
    if (selectedPresetKey) {
      const currentPreset = presets[selectedPresetKey];
      if (value.start.compare(currentPreset.value.start) === 0 && value.end.compare(currentPreset.value.end) === 0) {
        return;
      }
    }

    // Try to find a matching preset
    for (const [key, preset] of Object.entries(presets)) {
      if (value.start.compare(preset.value.start) === 0 && value.end.compare(preset.value.end) === 0) {
        setSelectedPresetKey(key as PresetDateRangeKey);
        return;
      }
    }

    // No matching preset found
    setSelectedPresetKey(null);
  }, [value, selectedPresetKey, presets]);

  // Use the selected preset key for label if available, otherwise show formatted date range
  const presetLabel = React.useMemo(() => {
    if (selectedPresetKey) {
      return presets[selectedPresetKey].label;
    }
    return null;
  }, [selectedPresetKey, presets]);
  const formattedDateRange = React.useMemo(() => getFormattedDateRange(value), [value]);

  const handlePresetClick = (presetKey: PresetDateRangeKey) => {
    const preset = presets[presetKey];
    setSelectedPresetKey(presetKey);
    setValue(preset.value);
    setIsOpen(false);
    setShowCustomPicker(false);
  };

  const handleCustomClick = () => {
    setShowCustomPicker(true);
  };

  const handleCustomApply = () => {
    if (internalDateValue) {
      setSelectedPresetKey(null);
      setValue({
        start: toCalendarDate(internalDateValue.start),
        end: toCalendarDate(internalDateValue.end),
      });
    }
    setIsOpen(false);
    setShowCustomPicker(false);
  };

  const handleCustomCancel = () => {
    setShowCustomPicker(false);
  };

  const handleInternalDateValueChange = (range: { start: DateValue; end: DateValue } | null) => {
    if (range) {
      // Ensure start date doesn't go beyond earliest valid date
      const startDate = toCalendarDate(range.start);
      const validatedStartDate = startDate.compare(earliestValidDate) < 0 ? earliestValidDate : startDate;

      // Ensure end date doesn't exceed today
      const endDate = toCalendarDate(range.end);
      const validatedEndDate = endDate.compare(now) > 0 ? now : endDate;

      // Update internal value with validated end date
      setInternalDateValue({
        start: validatedStartDate,
        end: validatedEndDate,
      });
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <AriaButton className="outline-hidden focus-visible:outline-focus-ring flex cursor-pointer items-center overflow-clip rounded-md border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2">
        {/* Date display section - now clickable */}
        <div className="bg-primary flex items-center gap-1 rounded-bl-md rounded-tl-md px-3 py-2">
          {presetLabel ? (
            <span className="text-secondary text-sm font-semibold">{presetLabel}</span>
          ) : (
            <span className="text-tertiary text-sm font-semibold">{formattedDateRange}</span>
          )}
        </div>
        {/* Button section - also clickable */}
        <div className="bg-brand-solid hover:bg-brand-solid_hover flex items-center justify-center rounded-bl-none rounded-br-md rounded-tl-none rounded-tr-md p-2">
          <ChevronDownIcon className="size-4.5 text-button-primary-icon" />
        </div>
      </AriaButton>

      <Dropdown.Popover className="w-fit" placement="bottom end">
        <AriaDialog className="flex" aria-label="Date range picker">
          {/* Preset list - always visible */}
          <div className="w-48 shrink-0">
            <div className="flex flex-col gap-1 p-2">
              {Object.entries(presets).map(([key, preset]) => (
                <RangePresetButton
                  key={key}
                  value={preset.value}
                  onClick={() => handlePresetClick(key as PresetDateRangeKey)}
                >
                  {preset.label}
                </RangePresetButton>
              ))}
              <Button
                slot={null}
                onClick={handleCustomClick}
                className={cx(
                  'outline-focus-ring! h-auto! cursor-pointer! justify-start! rounded-md! px-3! py-2! text-left! text-sm! font-medium! shadow-none! ring-0! transition! duration-100! ease-linear! focus-visible:outline-2! focus-visible:outline-offset-2!',
                  showCustomPicker
                    ? 'bg-active! text-secondary_hover! hover:bg-secondary_hover!'
                    : 'text-secondary! hover:bg-primary_hover! hover:text-secondary_hover! bg-transparent!'
                )}
              >
                Custom
              </Button>
            </div>
          </div>

          {/* Custom date picker - shown when Custom is selected */}
          {showCustomPicker && (
            <div className="border-secondary flex flex-col border-l">
              <AriaDateRangePicker
                value={internalDateValue}
                onChange={handleInternalDateValueChange}
                maxValue={now}
                minValue={earliestValidDate}
                aria-label="Custom date range picker"
              >
                <AriaRangeCalendar visibleDuration={{ months: 1 }} maxValue={now} defaultFocusedValue={now}>
                  <div className="flex flex-col gap-3 px-6 py-5">
                    <header className="relative flex items-center justify-between gap-2">
                      <Button
                        slot="previous"
                        iconLeading={ChevronLeftIcon}
                        size="sm"
                        color="tertiary"
                        className="size-8"
                      />
                      <div className="text-fg-secondary text-sm font-semibold">
                        <CalendarMonthTitle />
                      </div>
                      <Button
                        slot="next"
                        iconLeading={ChevronRightIcon}
                        size="sm"
                        color="tertiary"
                        className="size-8"
                      />
                    </header>

                    <AriaCalendarGrid weekdayStyle="short" className="w-max">
                      <AriaCalendarGridHeader>
                        {(day) => (
                          <AriaCalendarHeaderCell className="border-b-4 border-transparent p-0">
                            <div className="text-secondary flex size-10 items-center justify-center text-sm font-medium">
                              {day.slice(0, 2)}
                            </div>
                          </AriaCalendarHeaderCell>
                        )}
                      </AriaCalendarGridHeader>
                      <AriaCalendarGridBody className="[&_td]:p-0 [&_tr:last-of-type]:border-none [&_tr]:border-b-4 [&_tr]:border-transparent">
                        {(date) => <CalendarCell date={date} />}
                      </AriaCalendarGridBody>
                    </AriaCalendarGrid>
                  </div>

                  <div className="flex flex-col px-6 pb-5">
                    <AriaGroup className="flex items-center justify-center gap-2">
                      <DateInput slot="start" className="w-32 text-sm" />
                      <div className="text-quaternary text-sm" aria-hidden="true">
                        –
                      </div>
                      <DateInput slot="end" className="w-32 text-sm" />
                    </AriaGroup>
                  </div>

                  <div className="border-secondary flex gap-3 border-t px-4 py-3">
                    <Button slot={null} size="xs" color="secondary" onClick={handleCustomCancel} className="flex-1">
                      Cancel
                    </Button>
                    <Button slot={null} size="xs" color="primary" onClick={handleCustomApply} className="flex-1">
                      Apply
                    </Button>
                  </div>
                </AriaRangeCalendar>
              </AriaDateRangePicker>
            </div>
          )}
        </AriaDialog>
      </Dropdown.Popover>
    </DialogTrigger>
  );
};

export default DateRangePicker;

/**
 * Get the display text for a date range.
 * Returns the preset label if it matches, otherwise a formatted custom date range.
 */
export const useDateRangeDisplayText = (
  dateRange?: CalendarDateRange,
  defaultPreset: PresetDateRangeKey = 'thisYear'
): string => {
  const oldestTransactionDate = useOldestTransactionDate();
  const presetLabel = getPresetLabel(dateRange, defaultPreset, oldestTransactionDate);
  if (presetLabel) {
    return presetLabel;
  }
  return getFormattedDateRange(dateRange);
};

/**
 * Get a sentence fragment for empty state messages (e.g. "No transactions this month").
 * Flows naturally: "this month", "last year", "in the last 3 months", "between Oct 21, 2025 - Nov 5, 2025"
 */
export const useEmptyStateDateLabel = (dateRange?: CalendarDateRange): string => {
  const oldestTransactionDate = useOldestTransactionDate();

  if (!dateRange) {
    return '';
  }

  const presetLabels: Record<PresetDateRangeKey, string> = {
    thisMonth: 'this month',
    pastMonth: 'last month',
    past3Months: 'in the last 3 months',
    past6Months: 'in the last 6 months',
    thisYear: 'this year',
    pastYear: 'last year',
    allTime: 'across all data',
  };

  for (const preset of Object.keys(presetLabels) as PresetDateRangeKey[]) {
    if (isPresetDateRange(dateRange, preset, oldestTransactionDate)) {
      return presetLabels[preset];
    }
  }

  return `between ${getFormattedDateRange(dateRange)}`;
};

/**
 * Hook version of getPresetDateRange that uses the user's oldest transaction date.
 * This avoids falling back to default dates for the all-time preset in app runtime.
 */
export const usePresetDateRange = (preset: PresetDateRangeKey): CalendarDateRange => {
  const oldestTransactionDate = useOldestTransactionDate();
  return React.useMemo(() => getPresetDateRange(preset, oldestTransactionDate), [oldestTransactionDate, preset]);
};
